/**
 * @file  auth/index.ts
 * @description  authorization function
 *
 * @author ogarcia (ozkary)
 *
 */
import { getHeader } from '../../functions';
import * as _cache from '../../services/cache';
import { trace } from '../../services/logger';
import { Claims, PaymentToken, AuthToken } from './model';

/**
 * Asynchronous wait which enable us to retry an operation
 * @returns void
 */
const waitAsync = async (secs = 3): Promise<void> => {
  return new Promise((resolve) => {
    const delay = secs * 1000;
    const timer = setTimeout(() => {
      clearTimeout(timer);
      resolve();
    }, delay);
  });
};

/**
 * sets the auth token for route validation
 */
export const signIn = async (): Promise<string> => {
  const key = _cache.Key.auth;
  let token = _cache.getItem<string>(key);
  if (!token) {
    token = await getHeader('signIn', key);
    if (token) {
      _cache.setItem<string>(key, token);
    }
  }

  return token;
};

/**
 * Gets the current session token
 * @returns the encoded token
 */
export const getAuthToken = async (useToken = true, retry = 3): Promise<AuthToken> => {
  const key = _cache.Key.auth;
  const token = _cache.getItem<string>(key);
  if (!token && useToken && retry > 0) {
    await waitAsync(retry);
    // retry with a smaller delay until runs out
    return getAuthToken(useToken, --retry);
  }

  const authToken = {
    name: 'x-electron-auth',
    value: token
  };
  return authToken;
};

/**
 * gets the claims
 */
export const getClaims = async (): Promise<Claims> => {
  const token = await signIn();
  let claims: Claims = {};

  if (token) {
    const payload = token.split('.')[1];
    claims = JSON.parse(atob(payload)) as Claims;
  }
  return claims;
};

/**
 * gets the payment claims token
 */
export const getPaymentToken = async (): Promise<PaymentToken> => {
  const claims = await getClaims();
  const token: PaymentToken = {};

  if (claims && claims.payment) {
    const keys = Buffer.from(claims.payment).toString().split(':'); // format cred1:cred2

    if (keys && keys.length > 0) {
      token.key1 = keys[0];
      token.key2 = keys[1];
    }
  }
  trace(`payment token k1:${token.key1} k2:${token.key2}`);

  return token;
};
